<script setup>
import { computed, onBeforeUnmount, ref, watch, watchEffect } from 'vue';
import { useDevicesList, useUserMedia } from '@vueuse/core';
import CheckIcon from '@/Components/Icons/CheckIcon.vue';
import CrossIcon from '@/Components/Icons/CrossIcon.vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import { CAMERA_TYPES } from '@/Utils/vice-constants';
import CameraPermissionBlockedIcon from '@/Components/Icons/CameraPermissionBlockedIcon.vue';

const emit = defineEmits(['precheckPassed']);

const multiCameraDevicesStore = useLocalStreamingDevicesStore();

const {
  videoInputs: cameras,
  audioInputs: microphones,
  audioOutputs: speakers,
} = useDevicesList({
  requestPermissions: true,
});
const { stream, enabled, stop, start, audioDeviceId, videoDeviceId } = useUserMedia();

const video = ref();
const audioStream = ref();
const micLabel = ref('');
const micStatus = ref('');
const cameraStatus = ref('');
const speakerStatus = ref('');
const currentWidth = ref(0);
const loading = ref(false);
const micEnabled = ref(false);
const isUnmounted = ref(false);
const currentMicro = ref('empty');
const currentCamera = ref('empty');
const currentSpeaker = ref('empty');

let javascriptNode = null;

const areDevicesReady = computed(
  () => micStatus.value === 'success' && cameraStatus.value === 'success',
);

const showBar = () => {
  let maxLevelL = 0.5;
  let oldLevelL = 0.1;
  if (javascriptNode) {
    javascriptNode.onaudioprocess = () => null;
  }

  const constraints = { audio: { deviceId: currentMicro.value }, video: false };
  navigator.mediaDevices
    .getUserMedia(constraints)
    .then(async (currentStream) => {
      if (audioStream.value) {
        await stopLiveStream(audioStream);
        micStatus.value = '';
      }
      audioStream.value = currentStream;
      micStatus.value = 'success';
      micLabel.value = currentStream?.getAudioTracks()[0]?.label;

      const audioContext = new AudioContext();
      const microphone = audioContext.createMediaStreamSource(currentStream);

      javascriptNode = audioContext.createScriptProcessor(1024, 1, 1);
      microphone.connect(javascriptNode);
      javascriptNode.connect(audioContext.destination);

      javascriptNode.onaudioprocess = (event) => {
        const inputL = event.inputBuffer.getChannelData(0);
        let instantL;
        let sumL = 0.0;
        for (let i = 0; i < inputL.length; ++i) {
          sumL += inputL[i] * inputL[i];
        }
        instantL = Math.sqrt(sumL / inputL.length);
        maxLevelL = Math.max(maxLevelL, instantL);
        instantL = Math.max(instantL, oldLevelL - 0.008);
        oldLevelL = instantL;
        currentWidth.value = Math.max(0, Math.min(100, (instantL / maxLevelL) * 100));
      };
    })
    .catch((err) => {
      console.error(err);
    });
};

const stopLiveStream = async (liveStream) => {
  if (liveStream.value?.active) {
    await liveStream.value.getTracks().forEach((track) => {
      track.stop();
      liveStream.value.removeTrack(track);
    });
  }
};

watchEffect(() => {
  if (audioStream?.value?.active) {
    micEnabled.value = true;
  }
});

watchEffect(() => {
  if (video.value) {
    video.value.srcObject = stream.value;
  }
});

watchEffect(() => {
  if (areDevicesReady.value) {
    emit('precheckPassed');
  }
});

watch(
  () => currentMicro.value,
  () => {
    micStatus.value = 'default';
    audioDeviceId.value = currentMicro.value;
    try {
      loading.value = true;
      setTimeout(() => {
        loading.value = false;
      }, 1000);
      showBar();
      micStatus.value = 'success';
      multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].micDeviceId =
        currentMicro.value;
    } catch (error) {
      console.error('Device start failed:', error);
      micStatus.value = 'fail';
    }
  },
);

watch(
  () => currentCamera.value,
  () => {
    cameraStatus.value = 'default';
    stop();
    videoDeviceId.value = currentCamera.value;
    try {
      start();
      cameraStatus.value = 'success';
      multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].cameraDeviceId =
        currentCamera.value;
    } catch (error) {
      console.error('Device start failed:', error);
      cameraStatus.value = 'fail';
    }
  },
);

watch(
  () => currentSpeaker.value,
  () => {
    speakerStatus.value = 'success';
    multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].speakerDeviceId =
      currentSpeaker.value;
  },
);

watch(
  cameras,
  (newCameras) => {
    if (newCameras.length > 0) {
      const cachedCamId =
        multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].cameraDeviceId;

      const cachedCamAvailable = newCameras.find((cam) => cam.deviceId === cachedCamId);
      currentCamera.value = cachedCamAvailable ? cachedCamId : newCameras[0].deviceId;
    }
  },
  { immediate: true },
);

watch(
  microphones,
  (newMicrophones) => {
    if (newMicrophones.length > 0) {
      const cachedMicId =
        multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].micDeviceId;

      const cachedMicAvailable = newMicrophones.find((mic) => mic.deviceId === cachedMicId);
      currentMicro.value = cachedMicAvailable ? cachedMicId : newMicrophones[0].deviceId;
    }
  },
  { immediate: true },
);

watch(
  speakers,
  (newSpeakers) => {
    if (newSpeakers.length > 0) {
      const cachedSpeakerId =
        multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].speakerDeviceId;

      const cachedSpeakerAvailable = newSpeakers.find((spk) => spk.deviceId === cachedSpeakerId);
      currentSpeaker.value = cachedSpeakerAvailable ? cachedSpeakerId : newSpeakers[0].deviceId;
    }
  },
  { immediate: true },
);

onBeforeUnmount(async () => {
  isUnmounted.value = true;
  if (audioStream?.value?.active) {
    await stopLiveStream(audioStream);
  }
  stop();
});
</script>

<template>
  <div class="flex flex-col gap-2 text-left">
    <div class="w-full inline-flex font-semibold text-md text-gray-900 justify-between pb-3">
      <span>Mandatory platform requirements include:</span>
    </div>
    <!--  Camera  -->
    <div class="rounded-xl relative w-full h-0" style="padding-top: 75%">
      <div class="absolute h-full w-full bottom-0 left-0">
        <div
          class="w-full absolute top-3 left-1 bg-white border rounded-xl p-1 h-auto mx-2 z-10"
          style="width: 94%"
        >
          <span v-if="cameraStatus === 'success'">
            <CheckIcon
              style="background-color: rgb(62 183 166)"
              class="absolute transition z-10 text-white rounded-full"
            />
          </span>
          <span v-else-if="cameraStatus === 'fail'">
            <CrossIcon class="absolute transition z-10 bg-red-100 text-red-800 rounded-full" />
          </span>
          <label class="flex h-4 text-xxs font-semibold text-vice-teal-900 pl-5">
            Camera:
            <select
              id="select-camera"
              v-model="currentCamera"
              class="
                py-1
                pl-0
                pr-6
                leading-065
                focus:ring-0
                focus:border-0
                flex
                absolute
                left-18
                text-xxs
                border-0
                rounded-md
                w-72
              "
            >
              <option v-for="camera of cameras" :key="camera.deviceId" :value="camera.deviceId">
                {{ camera.label }}
              </option>
            </select>
          </label>
        </div>

        <video ref="video" muted autoplay class="w-full h-full rounded-xl object-cover border" />
        <!--  Microphone  -->
        <div
          class="border px-1 rounded-xl bg-white absolute bottom-10 mx-2 left-1"
          style="width: 94%"
        >
          <div class="w-full inline-flex justify-start">
            <span v-if="micStatus === 'success'" class="pt-1">
              <CheckIcon
                style="background-color: rgb(62 183 166)"
                class="absolute transition z-10 text-white rounded-full"
              />
            </span>
            <span v-if="micStatus === 'fail'" class="pt-1">
              <CrossIcon class="absolute transition z-10 bg-red-100 text-red-800 rounded-full" />
            </span>
            <label class="flex h-4 text-xxs font-semibold text-vice-teal-900 pl-5 pt-1">
              Microphone:
              <select
                id="select-microphone"
                v-model="currentMicro"
                class="
                  py-1
                  pl-0
                  pr-6
                  leading-065
                  focus:ring-0
                  focus:border-0
                  flex
                  absolute
                  left-25
                  text-xxs
                  border-0
                  rounded-md
                  w-68
                "
              >
                <option
                  v-for="microphone of microphones"
                  :key="microphone.deviceId"
                  :value="microphone.deviceId"
                >
                  {{ microphone.label }}
                </option>
              </select>
            </label>
          </div>
          <div class="rounded overflow-hidden relative w-auto mx-1 h-0 pb-3 pt-3">
            <div
              v-if="micEnabled"
              class="
                absolute
                bg-white
                rounded-lg
                h-3
                left-0
                right-0
                top-1/2
                transform
                -translate-y-1/2
                overflow-hidden
                border border-gray-300
              "
            >
              <div
                class="absolute bg-vice-teal-400 rounded-lg h-full top-0 left-0"
                :style="{ width: `${currentWidth}%` }"
              ></div>
            </div>
            <div
              v-if="!micEnabled"
              class="
                absolute
                top-0
                left-0
                w-full
                h-full
                flex flex-col
                items-center
                text-sm
                justify-center
                z-10
              "
            >
              <span class="flex items-center">
                <svg
                  class="animate-spin -ml-1 h-8 w-8"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="#14B8A6"
                    stroke-width="4"
                  ></circle>
                  <path
                    class=""
                    fill="#14B8A6"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </span>
            </div>
          </div>
        </div>
        <!-- Speakers -->
        <div
          class="w-full absolute left-1 bottom-2 bg-white border rounded-xl p-1 h-auto mx-2 z-10"
          style="width: 94%"
        >
          <span v-if="speakerStatus === 'success'">
            <CheckIcon
              style="background-color: rgb(62 183 166)"
              class="absolute transition z-10 text-white rounded-full"
            />
          </span>
          <span v-else-if="speakerStatus === 'fail'">
            <CrossIcon class="absolute transition z-10 bg-red-100 text-red-800 rounded-full" />
          </span>
          <label class="flex h-4 text-xxs font-semibold text-vice-teal-900 pl-5">
            Speaker:
            <select
              id="select-camera"
              v-model="currentSpeaker"
              class="
                py-1
                pl-1
                pr-6
                leading-065
                focus:ring-0
                focus:border-0
                flex
                absolute
                left-18
                text-xxs
                border-0
                rounded-md
                w-72
              "
            >
              <option v-for="speaker of speakers" :key="speaker.deviceId" :value="speaker.deviceId">
                {{ speaker.label }}
              </option>
            </select>
          </label>
        </div>
      </div>
      <div
        v-if="!enabled || loading"
        class="
          rounded-xl
          absolute
          bg-gray-700
          top-0
          left-0
          w-full
          h-full
          flex flex-col
          items-center
          text-sm
          justify-center
          z-10
        "
      >
        <div v-if="loading">
          <span class="flex items-center">
            <svg
              class="animate-spin -ml-1 h-8 w-8"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="#14B8A6"
                stroke-width="4"
              ></circle>
              <path
                class=""
                fill="#14B8A6"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </span>
        </div>
        <div v-if="cameras.length < 1 && !loading">
          <div class="flex text-center w-full justify-center">
            <CameraPermissionBlockedIcon class="h-24 w-24" />
          </div>
          <div class="flex text-center w-full justify-center">
            <span class="text-white text-center">
              A
              <span class="font-bold">camera</span>
              is needed to join the VCE session
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.left-18 {
  left: 4.75rem;
}
.left-25 {
  left: 6.25rem;
}
.w-68 {
  width: 16.5rem;
}
.leading-065 {
  line-height: 0.65rem;
}
video {
  transform: rotateY(180deg);
  -webkit-transform: rotateY(180deg);
  -moz-transform: rotateY(180deg);
}
</style>
